import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ShieldCheck, MessageSquareText, Send, Loader2, PartyPopper } from "lucide-react";
import { BRAND, DISCLOSURE_COPY, WELCOME_MESSAGE } from "@/constants/compliance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DisclosureRow = ({ label, value, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b border-[#1E2022]/8 last:border-0">
    <span className="font-mono2 text-[11px] tracking-[0.15em] uppercase text-[#8A8F98] sm:w-40 shrink-0">
      {label}
    </span>
    <span data-testid={testId} className="text-sm sm:text-base font-medium text-[#1E2022]">
      {value}
    </span>
  </div>
);

const OptInForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", preference: "Morning & evening tasks" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10) {
      setError("Please enter your name and a valid 10-digit mobile number.");
      return;
    }
    if (!consent) {
      setError("Please check the consent box to agree to receive automated text messages.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/optin`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        task_preference: form.preference,
        consent,
      });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="optin-success"
        className="bg-white rounded-3xl p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-[#4A7C59]/20"
      >
        <div className="w-12 h-12 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] flex items-center justify-center mb-5">
          <PartyPopper size={22} />
        </div>
        <h3 className="font-display font-bold text-2xl text-[#1E2022] mb-3">
          You're on the list, {form.name.split(" ")[0]}.
        </h3>
        <p className="text-sm text-[#575B60] leading-relaxed mb-5">
          Here is the confirmation message your phone will receive:
        </p>
        <div className="bg-[#FAF5EF] border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3">
          <p className="text-xs leading-relaxed text-[#575B60]">{WELCOME_MESSAGE}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="optin-form"
      className="bg-white rounded-3xl p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-stone-200/70"
    >
      <h3 className="font-display font-bold text-2xl text-[#1E2022] mb-1.5">
        Opt in online instead
      </h3>
      <p className="text-sm text-[#575B60] mb-7">
        Prefer to sign up here? Fill this out and check the consent box.
      </p>

      <label className="block mb-4">
        <span className="font-mono2 text-[11px] tracking-[0.15em] uppercase text-[#8A8F98]">Full name</span>
        <input
          data-testid="optin-name-input"
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Jordan Rivers"
          className="mt-1.5 w-full rounded-xl border border-stone-200 bg-[#FAF8F5] px-4 py-3 text-sm text-[#1E2022] outline-none focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/15 transition"
        />
      </label>

      <label className="block mb-4">
        <span className="font-mono2 text-[11px] tracking-[0.15em] uppercase text-[#8A8F98]">Mobile number</span>
        <input
          data-testid="optin-phone-input"
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+1 555 123 4567"
          className="mt-1.5 w-full rounded-xl border border-stone-200 bg-[#FAF8F5] px-4 py-3 text-sm text-[#1E2022] outline-none focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/15 transition"
        />
      </label>

      <label className="block mb-6">
        <span className="font-mono2 text-[11px] tracking-[0.15em] uppercase text-[#8A8F98]">Reminder preference</span>
        <select
          data-testid="optin-preference-select"
          value={form.preference}
          onChange={(e) => setForm({ ...form, preference: e.target.value })}
          className="mt-1.5 w-full rounded-xl border border-stone-200 bg-[#FAF8F5] px-4 py-3 text-sm text-[#1E2022] outline-none focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/15 transition"
        >
          <option>Morning & evening tasks</option>
          <option>Workday reminders only</option>
          <option>All my tasks, all day</option>
        </select>
      </label>

      <label
        data-testid="optin-consent-label"
        className="flex items-start gap-3 cursor-pointer select-none mb-2"
      >
        <input
          data-testid="optin-consent-checkbox"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 w-5 h-5 shrink-0 rounded-md border-stone-300 accent-[#D96B43] cursor-pointer"
        />
        <span className="text-sm text-[#575B60] leading-relaxed">
          I agree to receive recurring automated task reminder text messages
          from {BRAND.name} at the mobile number provided. Consent is not a
          condition of purchase.
        </span>
      </label>

      <p className="text-xs text-[#8A8F98] leading-relaxed mb-6 pl-8" data-testid="optin-legal-links">
        By checking the box you accept our{" "}
        <Link to="/terms" data-testid="optin-terms-link" className="text-[#D96B43] underline underline-offset-2 hover:text-[#C25832]">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link to="/privacy" data-testid="optin-privacy-link" className="text-[#D96B43] underline underline-offset-2 hover:text-[#C25832]">
          Privacy Policy
        </Link>
        . Msg frequency varies. Msg & data rates may apply. Reply HELP for
        help, STOP to cancel.
      </p>

      {error && (
        <p data-testid="optin-error" className="text-sm text-red-600 mb-4">
          {error}
        </p>
      )}

      <button
        data-testid="optin-submit-button"
        type="submit"
        disabled={status === "loading"}
        className="w-full font-display font-bold bg-[#1E2022] text-[#FAF8F5] rounded-full px-7 py-4 text-base hover:bg-[#D96B43] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {status === "loading" ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Send size={17} />
        )}
        Sign me up for reminders
      </button>
    </form>
  );
};

export const ComplianceSection = () => (
  <section
    id="optin"
    data-testid="compliance-section"
    className="relative bg-[#F3EFEA] py-24 sm:py-36 grain"
  >
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="font-mono2 text-xs tracking-[0.25em] uppercase text-[#4A7C59] mb-4"
      >
        SMS opt-in
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-[#1E2022] max-w-3xl leading-[1.08]"
        data-testid="compliance-heading"
      >
        One keyword. <span className="italic text-[#D96B43]">Full transparency.</span>
      </motion.h2>

      <div className="mt-14 sm:mt-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
        >
          <div
            data-testid="sms-disclosure-box"
            className="bg-[#FAF5EF] rounded-3xl p-8 sm:p-10 border-2 border-[#D96B43]/30 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-11 h-11 rounded-full bg-[#D96B43] text-white flex items-center justify-center">
                <ShieldCheck size={20} />
              </span>
              <div>
                <h3 className="font-display font-bold text-xl text-[#1E2022] leading-tight">
                  SMS Opt-In Disclosure
                </h3>
                <p className="text-xs text-[#8A8F98]">Carrier-compliant messaging program</p>
              </div>
            </div>

            <p data-testid="disclosure-copy" className="text-sm sm:text-base text-[#575B60] leading-relaxed mb-7">
              {DISCLOSURE_COPY}
            </p>

            <div className="border-t border-[#1E2022]/10 pt-2">
              <DisclosureRow label="Keyword" value={`Text ${BRAND.keyword} to ${BRAND.number}`} testId="disclosure-keyword" />
              <DisclosureRow label="Toll-free" value={`Or text ${BRAND.keyword} to ${BRAND.numberTollfree} — same program`} testId="disclosure-tollfree" />
              <DisclosureRow label="Program" value="Automated task reminders & schedule updates" testId="disclosure-program" />
              <DisclosureRow label="Frequency" value="Message frequency varies based on your tasks" testId="disclosure-frequency" />
              <DisclosureRow label="Rates" value="Message & data rates may apply" testId="disclosure-rates" />
              <DisclosureRow label="Help" value="Reply HELP for assistance" testId="disclosure-help" />
              <DisclosureRow label="Opt out" value="Reply STOP to cancel at any time" testId="disclosure-stop" />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/terms"
                data-testid="disclosure-terms-link"
                className="text-xs font-semibold text-[#D96B43] border border-[#D96B43]/40 rounded-full px-4 py-2 hover:bg-[#D96B43] hover:text-white transition-colors duration-300"
              >
                Terms of Service
              </Link>
              <Link
                to="/privacy"
                data-testid="disclosure-privacy-link"
                className="text-xs font-semibold text-[#D96B43] border border-[#D96B43]/40 rounded-full px-4 py-2 hover:bg-[#D96B43] hover:text-white transition-colors duration-300"
              >
                Privacy Policy
              </Link>
            </div>
          </div>

          <div className="mt-6 flex items-start gap-3 bg-white/70 rounded-2xl p-5 border border-stone-200/70">
            <MessageSquareText size={18} className="text-[#4A7C59] shrink-0 mt-0.5" />
            <p className="text-xs text-[#575B60] leading-relaxed" data-testid="welcome-message-preview">
              <span className="font-semibold text-[#1E2022]">Welcome message you receive after texting {BRAND.keyword}:</span>{" "}
              {WELCOME_MESSAGE}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <OptInForm />
        </motion.div>
      </div>
    </div>
  </section>
);
